import { getTimeZone, setTimeZone } from '@shared/lib/datetime/time-zone'
import { createContext, Fragment, type ReactNode, useContext, useEffect } from 'react'
import { defaultBranding } from '../lib/default-branding'
import type { TenantBrandingResponse } from '../types/branding.types'
import { useTenantBrandingBootstrap } from './useTenantBranding'

interface TenantBrandingContextValue {
  branding: TenantBrandingResponse
  isLoading: boolean
}

const TenantBrandingContext = createContext<TenantBrandingContextValue>({
  branding: defaultBranding,
  isLoading: false,
})

const colorTokens = [
  'background',
  'foreground',
  'card',
  'card-foreground',
  'popover',
  'popover-foreground',
  'primary',
  'primary-foreground',
  'secondary',
  'secondary-foreground',
  'muted',
  'muted-foreground',
  'accent',
  'accent-foreground',
  'destructive',
  'destructive-foreground',
  'border',
  'input',
  'ring',
] as const

function applyBrandingCssVariables(branding: TenantBrandingResponse) {
  if (typeof document === 'undefined') {
    return
  }

  const root = document.documentElement
  const light = branding.branding.light
  const dark = branding.branding.dark

  const values = {
    light: {
      background: light.background,
      foreground: light.foreground,
      card: light.card,
      'card-foreground': light.cardForeground,
      popover: light.popover,
      'popover-foreground': light.popoverForeground,
      primary: light.primary,
      'primary-foreground': light.primaryForeground,
      secondary: light.secondary,
      'secondary-foreground': light.secondaryForeground,
      muted: light.muted,
      'muted-foreground': light.mutedForeground,
      accent: light.accent,
      'accent-foreground': light.accentForeground,
      destructive: light.destructive,
      'destructive-foreground': light.destructiveForeground,
      border: light.border,
      input: light.input,
      ring: light.ring,
    },
    dark: {
      background: dark.background,
      foreground: dark.foreground,
      card: dark.card,
      'card-foreground': dark.cardForeground,
      popover: dark.popover,
      'popover-foreground': dark.popoverForeground,
      primary: dark.primary,
      'primary-foreground': dark.primaryForeground,
      secondary: dark.secondary,
      'secondary-foreground': dark.secondaryForeground,
      muted: dark.muted,
      'muted-foreground': dark.mutedForeground,
      accent: dark.accent,
      'accent-foreground': dark.accentForeground,
      destructive: dark.destructive,
      'destructive-foreground': dark.destructiveForeground,
      border: dark.border,
      input: dark.input,
      ring: dark.ring,
    },
  } as const

  for (const token of colorTokens) {
    root.style.setProperty(`--tenant-light-${token}`, values.light[token])
    root.style.setProperty(`--tenant-dark-${token}`, values.dark[token])
  }
}

interface TenantBrandingProviderProps {
  children: ReactNode
}

export function TenantBrandingProvider({ children }: TenantBrandingProviderProps) {
  const { data, isLoading } = useTenantBrandingBootstrap()
  const branding = data ?? defaultBranding
  useEffect(() => {
    applyBrandingCssVariables(branding)
  }, [branding])

  // Definido antes de os filhos renderizarem: toda data da aplicação usa o fuso do tenant.
  // A troca do fuso remonta a árvore para que nenhuma tela fique com horários no fuso antigo.
  setTimeZone(branding.timezone)

  return (
    <TenantBrandingContext.Provider value={{ branding, isLoading }}>
      <Fragment key={getTimeZone()}>{children}</Fragment>
    </TenantBrandingContext.Provider>
  )
}

export function useTenantBrandingContext() {
  return useContext(TenantBrandingContext)
}
